import { useCallback, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { isDiscontinued, DISCONTINUED_TITLE } from "@/lib/discontinued-products";

export type WishlistItem = {
  productId: string;
  psId: number | null;
  name: string;
  price: number;
  imageUrl: string | null;
  originalPrice?: number | null;
};

// In-memory only store. Wishlist data is NEVER persisted to the browser
// (no localStorage); it lives only in memory for the current session and is
// synced exclusively with the signed-in account (see WishlistSync). On logout
// the store is cleared, so favorites disappear until the user signs back in.
let memoryItems: WishlistItem[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function setMemoryItems(updater: WishlistItem[] | ((prev: WishlistItem[]) => WishlistItem[])) {
  memoryItems =
    typeof updater === "function"
      ? (updater as (p: WishlistItem[]) => WishlistItem[])(memoryItems)
      : updater;
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useWishlist() {
  const items = useSyncExternalStore(
    subscribe,
    () => memoryItems,
    () => memoryItems as WishlistItem[],
  );
  const setItems = setMemoryItems;
  const { user, loading } = useAuth();

  const has = useCallback(
    (id: string) => items.some((i) => i.productId === id),
    [items],
  );

  const toggle = useCallback(
    (item: WishlistItem) => {
      const exists = items.some((i) => i.productId === item.productId);
      if (!exists && isDiscontinued(item.psId, item.name)) {
        toast(DISCONTINUED_TITLE, {
          description:
            "Οι πολυσυσκευασίες εμφιαλωμένου νερού 6x1,5lt · 24x500ml · 12x500ml δεν διατίθενται πλέον.",
          duration: 6000,
        });
        return;
      }
      // Τα αγαπημένα απαιτούν λογαριασμό — δεν αποθηκεύονται τοπικά στον browser.
      if (!user && !loading) {
        toast("🐼 Ψιτ! Θέλω να σε γνωρίσω πρώτα!", {
          description:
            "Φτιάξε λογαριασμό ή σύνδεσου για να κρατήσει ο Mr Panda τα αγαπημένα σου ασφαλή σε κάθε συσκευή! 🎋💚",
          duration: 8000,
          action: {
            label: "Σύνδεση",
            onClick: () => {
              window.location.href = "/login";
            },
          },
        });
        return;
      }
      setItems((prev) =>
        prev.some((i) => i.productId === item.productId)
          ? prev.filter((i) => i.productId !== item.productId)
          : [item, ...prev],
      );
    },
    [items, user, loading],
  );

  const remove = useCallback(
    (id: string) => setItems((prev) => prev.filter((i) => i.productId !== id)),
    [],
  );

  const replace = useCallback((next: WishlistItem[]) => setItems(next), []);

  return { items, has, toggle, remove, replace, count: items.length };
}